import React from 'react';

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  success?: string;
  hint?: string;
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  error,
  success,
  hint,
  children
}) => {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-300">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-sm text-red-400 flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">error</span>
          {error}
        </p>
      )}
      {success && !error && (
        <p className="text-sm text-green-400 flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">check_circle</span>
          {success}
        </p>
      )}
      {hint && !error && !success && (
        <p className="text-sm text-gray-500">{hint}</p>
      )}
    </div>
  );
};

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  success?: boolean;
}

export const Input: React.FC<InputProps> = ({ 
  error, 
  success, 
  className = '', 
  ...props 
}) => {
  const baseClasses = `
    w-full px-4 py-2.5 rounded-lg
    bg-[#1a2332] text-gray-100 
    border transition-colors
    focus:outline-none focus:ring-2
    disabled:opacity-50 disabled:cursor-not-allowed
  `;
  
  let stateClasses = 'border-gray-700 hover:border-gray-600 focus:border-blue-500 focus:ring-blue-500/20';
  
  if (error) {
    stateClasses = 'border-red-500 focus:border-red-400 focus:ring-red-500/20';
  } else if (success) {
    stateClasses = 'border-green-500 focus:border-green-400 focus:ring-green-500/20';
  }
  
  return (
    <input
      className={`${baseClasses} ${stateClasses} ${className}`}
      {...props}
    />
  );
};

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  success?: boolean;
}

export const TextArea: React.FC<TextAreaProps> = ({ 
  error, 
  success, 
  className = '', 
  ...props 
}) => {
  const baseClasses = `
    w-full px-4 py-2.5 rounded-lg
    bg-[#1a2332] text-gray-100 
    border transition-colors
    focus:outline-none focus:ring-2
    disabled:opacity-50 disabled:cursor-not-allowed
    resize-none
  `;
  
  let stateClasses = 'border-gray-700 hover:border-gray-600 focus:border-blue-500 focus:ring-blue-500/20';
  
  if (error) {
    stateClasses = 'border-red-500 focus:border-red-400 focus:ring-red-500/20';
  } else if (success) {
    stateClasses = 'border-green-500 focus:border-green-400 focus:ring-green-500/20';
  }
  
  return (
    <textarea
      className={`${baseClasses} ${stateClasses} ${className}`}
      {...props}
    />
  );
};

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  success?: boolean;
}

export const Select: React.FC<SelectProps> = ({ 
  error, 
  success, 
  className = '', 
  children,
  ...props 
}) => {
  const baseClasses = `
    w-full px-4 py-2.5 rounded-lg
    bg-[#1a2332] text-gray-100 
    border transition-colors
    focus:outline-none focus:ring-2
    disabled:opacity-50 disabled:cursor-not-allowed
    appearance-none cursor-pointer
  `;
  
  let stateClasses = 'border-gray-700 hover:border-gray-600 focus:border-blue-500 focus:ring-blue-500/20';
  
  if (error) {
    stateClasses = 'border-red-500 focus:border-red-400 focus:ring-red-500/20';
  } else if (success) {
    stateClasses = 'border-green-500 focus:border-green-400 focus:ring-green-500/20';
  }
  
  return (
    <div className="relative">
      <select
        className={`${baseClasses} ${stateClasses} ${className}`}
        {...props}
      >
        {children}
      </select>
      <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        expand_more
      </span>
    </div>
  );
};

